/**
 * PA-COLLECTOR-AUTOMATION-001 P3 — compact, read-only collector status for Tax Processors /
 * Tax Search Team Leaders (any role without collector-assignment write permission).
 * No management controls; a quiet "Report issue" link remains unless an equivalent review is pending.
 */
import React from 'react';
import { CheckCircle2, Info, Clock, AlertTriangle, XCircle, Flag } from 'lucide-react';

const TONES = {
  verified: { wrap: 'border-emerald-200/70 bg-emerald-50/50 text-emerald-900', icon: 'text-emerald-600', Icon: CheckCircle2 },
  info:     { wrap: 'border-slate-200 bg-slate-50 text-slate-700', icon: 'text-slate-500', Icon: Info },
  pending:  { wrap: 'border-sky-200 bg-sky-50/60 text-sky-900', icon: 'text-sky-600', Icon: Clock },
  warn:     { wrap: 'border-amber-200 bg-amber-50 text-amber-900', icon: 'text-amber-600', Icon: AlertTriangle },
  error:    { wrap: 'border-red-200 bg-red-50 text-red-900', icon: 'text-red-600', Icon: XCircle },
};

export function compactStatus({ confidence, ambiguous, pendingReview, payee }) {
  if (ambiguous) return { key: 'ambiguous', tone: 'warn' };
  if (confidence === 'high') return { key: 'high', tone: 'verified' };
  if (confidence === 'medium') return { key: 'medium', tone: 'verified' };
  if (confidence === 'none') return { key: 'none', tone: 'error' };
  if (pendingReview) return { key: 'pending', tone: 'pending' };
  if (!payee) return { key: 'low_no_payee', tone: 'warn' };
  return { key: 'low', tone: 'info' };
}

const HEADLINE = {
  high: () => 'Verified collector',
  medium: () => 'Verified collector (generic category)',
  pending: () => 'Collector awaiting verification',
  low: () => 'Collector not yet verified',
  low_no_payee: (j) => `Payee not on file for ${j} — payment/MLC may be affected`,
  none: () => 'No collector or payee on file — notify the Jurisdiction Manager',
  ambiguous: (_j, cat) => `Ambiguous ${cat ? `${cat} ` : ''}collector — add the property municipality`,
};

export const CollectorGuidanceCompact = ({
  confidence, ambiguous, pendingReview, collectorName, payee, jurisdictionName, categoryLabel, canReport, onReport,
  // ADMIN-COLLECTOR-COMPACT-001 — optional admin-only slots: inline actions (right) + collapsible details (below).
  actions = null, children = null, headlineOverride = null,
}) => {
  const { key, tone } = compactStatus({ confidence, ambiguous, pendingReview, payee });
  const t = TONES[tone];
  const showCollector = key !== 'none' && key !== 'low_no_payee';
  return (
    <div
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2.5 py-1.5 text-[12px] leading-snug ${t.wrap}`}
      role="status"
      aria-live="polite"
      data-testid={`collector-guidance-compact-${key}`}
    >
      <t.Icon className={`h-3.5 w-3.5 flex-shrink-0 ${t.icon}`} />
      <span className="font-medium" data-testid="collector-guidance-compact-headline">
        {headlineOverride || HEADLINE[key](jurisdictionName || 'this jurisdiction', categoryLabel)}
      </span>
      {showCollector && (
        <>
          <span className="opacity-40">·</span>
          <span className="truncate" data-testid="collector-guidance-compact-collector">{collectorName || '—'}</span>
          <span className="opacity-40">·</span>
          <span className="truncate" data-testid="collector-guidance-compact-payee">
            Payable to <strong>{payee || '—'}</strong>
          </span>
        </>
      )}
      {key === 'low_no_payee' && collectorName && (
        <>
          <span className="opacity-40">·</span>
          <span className="truncate" data-testid="collector-guidance-compact-collector">{collectorName}</span>
        </>
      )}
      <span className="ml-auto flex items-center gap-2">
        {pendingReview ? (
          <span className="text-[11px] opacity-80" data-testid="collector-guidance-compact-review-in-progress" title={`Review item ${pendingReview.id}`}>
            Review in progress · CCR-{String(pendingReview.id).slice(0, 8).toUpperCase()}
          </span>
        ) : canReport ? (
          <button
            type="button"
            onClick={onReport}
            className="inline-flex items-center gap-1 text-[11px] underline-offset-2 opacity-80 hover:underline hover:opacity-100"
            data-testid="collector-guidance-report-issue-link"
          >
            <Flag className="h-3 w-3" /> Report issue
          </button>
        ) : null}
        {actions}
      </span>
      {children ? <div className="basis-full" data-testid="collector-guidance-compact-details">{children}</div> : null}
    </div>
  );
};

export default CollectorGuidanceCompact;
